import React, { useState } from 'react';
import { FiHelpCircle } from 'react-icons/fi';
import { Button, HelpWindow, XAxisToggle, ZonesTab, ZoneButton, zonesHelpTopics } from '../../design-system';
import ProjectShell from '../../components/layout/ProjectShell';
import SamplesSummary from '../../components/correlation/SamplesSummary';
import { useSelected } from '../../providers/selectedContext';
import { ReactComponent as EmptyIllustration } from '../../assets/empty-chart-zones.svg';
import styles from './ZonesPage.module.css';

type IconProps = { size?: number; color?: string; style?: React.CSSProperties };
const HelpIcon = FiHelpCircle as React.FC<IconProps>;

const ZonesContent = ({ onHelpOpen }: { onHelpOpen: () => void }) => {
  const { selectedInfo } = useSelected();

  const zones = selectedInfo.project?.correlation?.zones;

  const pageActions = (
    <>
      <ZoneButton />
      <Button variant="secondary" startIcon={<HelpIcon size={14} />} onClick={onHelpOpen}>
        Help
      </Button>
    </>
  );

  if (!zones || zones.length === 0) {
    return (
      <div className={styles.emptyState}>
        <EmptyIllustration style={{ width: '420px' }} />
        <p className={styles.emptyTitle}>No zones created</p>
        <p className={styles.emptySubtitle}>
          Create flow unit zones to start the analysis.
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          <XAxisToggle />
          {pageActions}
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%', overflow: 'hidden' }}>
      {/* Single controls line: sample chips on the left, X-axis toggle plus the
          Rock Type Settings / Help actions on the right — all on the line below
          instead of a separate grey header row. */}
      <div className={styles.stickyBar}>
        <SamplesSummary axisControl={<XAxisToggle />} actions={pageActions} />
      </div>
      <div style={{ flexGrow: 1, minHeight: 0, overflowY: 'auto' }}>
        <ZonesTab />
      </div>
    </div>
  );
};

const ZonesPage = () => {
  const { selectedInfo } = useSelected();
  const [helpOpen, setHelpOpen] = useState(false);

  return (
    <ProjectShell>
      {!selectedInfo.project ? (
        <div className={styles.emptyState}>
          <EmptyIllustration style={{ width: '420px' }} />
          <p className={styles.emptyTitle}>No project selected</p>
          <p className={styles.emptySubtitle}>
            Select a project from the dashboard to view Rock Type analysis.
          </p>
        </div>
      ) : (
        <ZonesContent onHelpOpen={() => setHelpOpen(true)} />
      )}

      <HelpWindow
        topics={zonesHelpTopics}
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
      />
    </ProjectShell>
  );
};

export default ZonesPage;
